import { useFormState, useWatch } from 'react-hook-form';
import * as Icon from 'react-feather';
import { Button, Flex, Upload } from 'antd';
import { UploadRequestOption } from 'rc-upload/es/interface';
import {
  DescriptionInputDataKey,
  Modal,
  Padding,
  PermissionCode,
  useHasPermissionForDescription,
} from '@atlas-frontend/sdk';
import { useCallback } from 'react';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './InputDataUpload.translations.json';
import { useInputDataDetailsStoreValue } from './InputDataDetailsContext';

const { addTranslations, useTranslation } = i18nNsFactory('input-data-upload');
addTranslations(translations);

export function InputDataUpload({
  params,
  onUpload,
  disabled,
}: {
  params: DescriptionInputDataKey;
  onUpload: (request: UploadRequestOption) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { isDirty } = useFormState();
  const [, setErrors] = useInputDataDetailsStoreValue('mutationErrors');
  const rowCount = useWatch({
    name: 'rows.length',
  });

  const handleUpload = useCallback(
    (request: UploadRequestOption) => {
      if (!(isDirty || rowCount)) {
        setErrors(undefined);
        onUpload(request);
        return;
      }

      Modal.confirm({
        title: t('title'),
        content: t('description'),
        footer: (
          <Padding top>
            <Flex justify="end" gap={8}>
              <Button
                id="cancel-input-data-upload"
                type="default"
                onClick={Modal.destroyAll}
              >
                {t('button.no')}
              </Button>
              <Button
                id="upload-input-data"
                type="primary"
                onClick={() => {
                  Modal.destroyAll();
                  setErrors(undefined);
                  onUpload(request);
                }}
              >
                {t('button.yes')}
              </Button>
            </Flex>
          </Padding>
        ),
      });
    },
    [isDirty, onUpload, rowCount, setErrors, t],
  );

  const descriptionId = Number(params.descriptionId);
  const hasPermissionForDescription = useHasPermissionForDescription();

  if (
    Number.isNaN(descriptionId) ||
    !descriptionId ||
    !hasPermissionForDescription(
      PermissionCode.INPUT_DATA_MANAGE,
      descriptionId,
    )
  ) {
    return null;
  }

  return (
    <Upload
      id="input-data-upload"
      customRequest={handleUpload}
      showUploadList={false}
      disabled={disabled}
    >
      <Button id="upload-input-data" icon={<Icon.Upload size={18} />} />
    </Upload>
  );
}
